import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { FiX, FiSave, FiLoader } from 'react-icons/fi';
import { vendorCreditService } from '../../services/vendorCreditService';
import { billService } from '../../services/billService'; // Reuse to get vendors
import { CREDIT_REASONS } from '../../data/vendorCreditConstants';

const FieldRow = ({ label, required, children }) => (
  <div className="flex flex-col mb-4">
    <label className="text-[13px] font-bold text-slate-700 mb-1.5">
      {label} {required && <span className="text-rose-500">*</span>}
    </label>
    {children}
  </div>
);

export default function VendorCreditForm({ isOpen, onClose, initialData = null, onSuccess }) {
  const [vendors, setVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    vendor_id: '',
    credit_number: `VCR-${Math.floor(Math.random() * 90000) + 10000}`,
    issue_date: new Date().toISOString().split('T')[0],
    total_amount: '',
    reason: CREDIT_REASONS[0].id,
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, initialData]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const vendorsData = await billService.getVendors();
      setVendors(vendorsData || []);
      
      if (initialData) {
        setFormData({
          vendor_id: initialData.vendor_id,
          credit_number: initialData.credit_number,
          issue_date: initialData.issue_date,
          total_amount: initialData.total_amount,
          reason: initialData.reason || CREDIT_REASONS[0].id,
          notes: initialData.notes || ''
        });
      } else {
        setFormData(prev => ({
          ...prev,
          vendor_id: vendorsData.length > 0 ? vendorsData[0].id : '',
          credit_number: `VCR-${Math.floor(Math.random() * 90000) + 10000}`
        }));
      }
    } catch (error) {
      console.error("Failed to load form data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.vendor_id) {
      alert("Please select a vendor.");
      return;
    }
    
    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        total_amount: parseFloat(formData.total_amount) || 0
      };

      if (initialData?.id) {
        await vendorCreditService.updateCredit(initialData.id, payload);
      } else {
        await vendorCreditService.createCredit(payload);
      }
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("Failed to save credit:", error);
      alert("Error saving credit memo.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <h2 className="text-lg font-black text-slate-900">
            {initialData ? 'Edit Credit Memo' : 'New Credit Memo'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full text-slate-400 transition-colors">
            <SafeIcon icon={FiX} />
          </button>
        </div>
        
        {isLoading ? (
          <div className="p-12 flex justify-center">
            <SafeIcon icon={FiLoader} className="animate-spin text-3xl text-blue-600" />
          </div>
        ) : (
          <form onSubmit={handleSave} className="flex flex-col h-full">
            <div className="p-6 overflow-y-auto custom-scrollbar space-y-4">
              <FieldRow label="Vendor" required>
                <select 
                  required
                  value={formData.vendor_id}
                  onChange={(e) => setFormData({...formData, vendor_id: e.target.value})}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:border-blue-500"
                >
                  <option value="" disabled>Select Vendor</option>
                  {vendors.map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </FieldRow>

              <div className="grid grid-cols-2 gap-4">
                <FieldRow label="Credit Number" required>
                  <input 
                    type="text" 
                    required
                    value={formData.credit_number}
                    onChange={(e) => setFormData({...formData, credit_number: e.target.value})}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:border-blue-500"
                  />
                </FieldRow>
                <FieldRow label="Issue Date" required>
                  <input 
                    type="date" 
                    required
                    value={formData.issue_date}
                    onChange={(e) => setFormData({...formData, issue_date: e.target.value})}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:border-blue-500"
                  />
                </FieldRow>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FieldRow label="Total Amount" required>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                    <input 
                      type="number" 
                      required
                      min="0.01"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.total_amount}
                      onChange={(e) => setFormData({...formData, total_amount: e.target.value})}
                      className="w-full pl-8 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-black outline-none focus:border-blue-500"
                    />
                  </div>
                </FieldRow>
                <FieldRow label="Reason">
                  <select 
                    value={formData.reason}
                    onChange={(e) => setFormData({...formData, reason: e.target.value})}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:border-blue-500"
                  >
                    {CREDIT_REASONS.map(r => (
                      <option key={r.id} value={r.label}>{r.label}</option>
                    ))}
                  </select>
                </FieldRow>
              </div>

              <FieldRow label="Notes (Internal)">
                <textarea 
                  rows="3"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Additional details about this credit..."
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium outline-none focus:border-blue-500 resize-none"
                />
              </FieldRow>
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 mt-auto">
              <button 
                type="button" 
                onClick={onClose}
                className="px-6 py-2.5 rounded-full font-bold text-sm text-slate-500 hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={isSaving}
                className="px-8 py-2.5 bg-slate-900 text-white rounded-full font-bold text-sm shadow-lg hover:bg-black transition-all flex items-center gap-2 disabled:opacity-70"
              >
                {isSaving ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSave} />}
                Save Credit
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}